/**
 * FLEET SCREEN  (fleet managers only — e.g. Norman)
 *
 * Bottom-tab home for managing a fleet:
 *   - Fleet messages row  → opens the existing Fleet Inbox (reply on behalf of drivers)
 *   - My Fleet            → roster of drivers in this fleet + how many active jobs each holds
 *   - Active Jobs         → every job this manager accepted, with the current assignee and
 *                           an Assign / Reassign control that swaps the fleet driver.
 *
 * Reuses backend endpoints:
 *   GET  /drivers/me/fleet-members
 *   GET  /drivers/me/fleet-jobs
 *   POST /jobs/:id/assign-fleet   { fleetDriverId: 'self' | <memberId> }
 */
import React, { useState, useCallback } from 'react';
import {
  View, Text, ScrollView, TouchableOpacity, ActivityIndicator, RefreshControl,
  SafeAreaView, Modal, Alert, StyleSheet, Platform,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useSelector } from 'react-redux';
import { useFocusEffect } from '@react-navigation/native';
import { api } from '../../services/api';
import { RD } from '../../theme/colors';
import { RootState } from '../../store';
import { fmtDriverWindow } from '../../utils/windows';

type Member = {
  id: string; name: string; phone?: string; vehicleType?: string;
  status?: string; profilePhotoUrl?: string | null;
};
type FleetJob = {
  id: string; productTitle?: string; status: string; wooOrderId?: string;
  pickupAddress?: string; scheduledWindowStart?: string | null; driveTimeMinutes?: number | null;
  assignedFleetDriverId?: string | null; assignedFleetDriverName?: string | null;
};

function cityOf(addr?: string): string {
  if (!addr) return '';
  // "401 Bragg Drive, Front Royal, VA, USA" → "Front Royal, VA"
  const parts = addr.split(',').map(s => s.trim()).filter(Boolean);
  if (parts.length >= 3) return `${parts[parts.length - 3]}, ${parts[parts.length - 2]}`.replace(/, USA$/i, '');
  return parts.slice(0, 2).join(', ');
}

function fmtWhen(iso?: string | null): string {
  if (!iso) return 'Not scheduled';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return 'Not scheduled';
  return d.toLocaleString('en-US', { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

export function FleetScreen({ navigation }: any) {
  const driver = useSelector((s: RootState) => s.auth.driver) as any;
  const managerId = driver?.id;

  const [members, setMembers] = useState<Member[]>([]);
  const [jobs, setJobs] = useState<FleetJob[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [picker, setPicker] = useState<FleetJob | null>(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const [m, j] = await Promise.all([
        api.get('/drivers/me/fleet-members').then(r => r.data).catch(() => []),
        api.get('/drivers/me/fleet-jobs').then(r => r.data).catch(() => []),
      ]);
      setMembers(Array.isArray(m) ? m : []);
      setJobs(Array.isArray(j) ? j : []);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useFocusEffect(useCallback(() => { load(); }, [load]));

  const onRefresh = () => { setRefreshing(true); load(); };

  const jobsForMember = (id: string) =>
    jobs.filter(j => j.assignedFleetDriverId === id).length;

  const assigneeLabel = (j: FleetJob): string => {
    if (!j.assignedFleetDriverId) return 'Unassigned';
    if (j.assignedFleetDriverId === managerId) return 'You';
    return j.assignedFleetDriverName || 'Assigned';
  };

  const assign = async (job: FleetJob, fleetDriverId: string) => {
    setSaving(true);
    try {
      await api.post(`/jobs/${job.id}/assign-fleet`, { fleetDriverId });
      setPicker(null);
      await load();
    } catch (e: any) {
      Alert.alert('Could not reassign', e?.response?.data?.error || 'Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // Derived summary counts (reuse loaded data only)
  const onJobsCount = jobs.filter(j => !!j.assignedFleetDriverId).length;
  const unassignedJobs = jobs.filter(j => !j.assignedFleetDriverId);

  return (
    <SafeAreaView style={styles.safe}>
      {/* Header */}
      <View style={styles.header}>
        <View style={styles.headerTitleRow}>
          <Text style={styles.headerTitle}>Your Fleet</Text>
          <View style={styles.managerPill}>
            <Ionicons name="people" size={12} color={RD.maroon} />
            <Text style={styles.managerPillText}>Manager</Text>
          </View>
        </View>
        <Text style={styles.headerSub}>You assign and oversee every job for your drivers.</Text>
      </View>

      {loading ? (
        <ActivityIndicator size="large" color={RD.maroon} style={{ marginTop: 40 }} />
      ) : (
        <ScrollView
          contentContainerStyle={{ padding: 16, paddingBottom: Platform.OS === 'ios' ? 110 : 88 }}
          refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} tintColor={RD.maroon} />}
        >
          {/* Summary stat cards */}
          <View style={styles.statRow}>
            <View style={styles.statCard}>
              <Text style={[styles.statNum, { color: RD.maroon }]}>{members.length}</Text>
              <Text style={styles.statLabel}>Drivers</Text>
            </View>
            <View style={styles.statCard}>
              <Text style={[styles.statNum, { color: RD.money }]}>{onJobsCount}</Text>
              <Text style={styles.statLabel}>On jobs</Text>
            </View>
            <View style={styles.statCard}>
              <Text style={[styles.statNum, { color: RD.info }]}>{jobs.length}</Text>
              <Text style={styles.statLabel}>Active jobs</Text>
            </View>
          </View>

          {/* Fleet messages entry */}
          <TouchableOpacity
            onPress={() => navigation.navigate('FleetMessages')}
            style={styles.messagesCard}
            activeOpacity={0.85}
          >
            <View style={styles.messagesIcon}>
              <Ionicons name="chatbubbles" size={20} color="#fff" />
            </View>
            <View style={{ flex: 1 }}>
              <Text style={styles.messagesTitle}>Fleet messages</Text>
              <Text style={styles.messagesSub}>Reply to buyers and sellers on behalf of your drivers</Text>
            </View>
            <Ionicons name="chevron-forward" size={20} color={RD.textFaint} />
          </TouchableOpacity>

          {/* Jobs to assign */}
          {unassignedJobs.length > 0 && (
            <TouchableOpacity
              onPress={() => setPicker(unassignedJobs[0])}
              style={styles.toAssignCard}
              activeOpacity={0.85}
            >
              <View style={styles.toAssignIcon}>
                <Ionicons name="car" size={20} color="#fff" />
              </View>
              <View style={{ flex: 1 }}>
                <Text style={styles.toAssignTitle}>
                  {unassignedJobs.length} job{unassignedJobs.length === 1 ? '' : 's'} to assign
                </Text>
                <Text style={styles.toAssignSub} numberOfLines={1}>
                  {unassignedJobs[0].productTitle || `Order #${unassignedJobs[0].wooOrderId || unassignedJobs[0].id.slice(0, 6)}`}
                </Text>
              </View>
              <Ionicons name="chevron-forward" size={18} color={RD.amber} />
            </TouchableOpacity>
          )}

          {/* My Fleet */}
          <Text style={styles.eyebrow}>YOUR DRIVERS ({members.length})</Text>
          {members.length === 0 ? (
            <Text style={styles.emptyText}>No drivers in your fleet yet.</Text>
          ) : (
            <View style={{ marginBottom: 24 }}>
              {members.map(m => (
                <View key={m.id} style={styles.memberCard}>
                  <View style={styles.avatar}>
                    <Text style={styles.avatarText}>{(m.name || '?').charAt(0).toUpperCase()}</Text>
                  </View>
                  <View style={{ flex: 1 }}>
                    <View style={styles.memberNameRow}>
                      <Text style={styles.memberName}>{m.name}</Text>
                      <View style={styles.statusDotRow}>
                        <View style={[styles.statusDot, { backgroundColor: m.status ? RD.money : RD.g40 }]} />
                        <Text style={[styles.statusText, { color: m.status ? RD.money : RD.textFaint }]}>
                          {m.status || 'Idle'}
                        </Text>
                      </View>
                    </View>
                    <Text style={styles.memberSub}>{m.vehicleType || 'Driver'}</Text>
                  </View>
                  <View style={styles.memberCount}>
                    <Text style={styles.memberCountNum}>{jobsForMember(m.id)}</Text>
                    <Text style={styles.memberCountLabel}>active</Text>
                  </View>
                </View>
              ))}
            </View>
          )}

          {/* Active Jobs */}
          <Text style={styles.eyebrow}>ACTIVE JOBS ({jobs.length})</Text>
          {jobs.length === 0 ? (
            <Text style={styles.emptyText}>No active jobs. Jobs you accept will show here to assign.</Text>
          ) : (
            jobs.map(j => {
              const assigned = !!j.assignedFleetDriverId;
              return (
                <View key={j.id} style={styles.jobCard}>
                  <TouchableOpacity onPress={() => navigation.navigate('JobDetail', { jobId: j.id })} activeOpacity={0.7}>
                    <Text style={styles.jobTitle} numberOfLines={2}>
                      {j.productTitle || `Order #${j.wooOrderId || j.id.slice(0, 6)}`}
                    </Text>
                    <Text style={styles.jobMeta}>
                      {cityOf(j.pickupAddress)}{cityOf(j.pickupAddress) ? ' · ' : ''}{j.status.replace(/_/g, ' ')}
                    </Text>
                    <Text style={styles.jobMeta}>
                      <Ionicons name="time-outline" size={12} color={RD.textMuted} /> Pickup {fmtDriverWindow(j, true) ?? 'Not scheduled'}
                    </Text>
                  </TouchableOpacity>

                  <View style={styles.jobFooter}>
                    <View style={[styles.assigneePill, { backgroundColor: assigned ? RD.moneySoft : RD.amberSoft }]}>
                      <Ionicons name={assigned ? 'person' : 'person-outline'} size={13} color={assigned ? RD.money : RD.amber} />
                      <Text style={[styles.assigneePillText, { color: assigned ? RD.money : RD.amber }]}>{assigneeLabel(j)}</Text>
                    </View>
                    <TouchableOpacity
                      onPress={() => setPicker(j)}
                      style={styles.reassignBtn}
                      activeOpacity={0.85}
                    >
                      <Ionicons name="swap-horizontal" size={14} color="#fff" />
                      <Text style={styles.reassignBtnText}>{assigned ? 'Reassign' : 'Assign'}</Text>
                    </TouchableOpacity>
                  </View>
                </View>
              );
            })
          )}
        </ScrollView>
      )}

      {/* Assign / reassign picker */}
      <Modal visible={!!picker} transparent animationType="slide" onRequestClose={() => setPicker(null)}>
        <View style={styles.modalOverlay}>
          <View style={styles.modalSheet}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>Assign driver</Text>
              <TouchableOpacity onPress={() => setPicker(null)} style={{ padding: 4, minWidth: 44, minHeight: 44, justifyContent: 'center', alignItems: 'center' }} hitSlop={{ top: 12, bottom: 12, left: 12, right: 12 }} activeOpacity={0.7}>
                <Ionicons name="close" size={24} color={RD.textMuted} />
              </TouchableOpacity>
            </View>
            <Text style={styles.modalSub} numberOfLines={1}>{picker?.productTitle}</Text>

            <ScrollView>
              {/* Myself */}
              <TouchableOpacity
                disabled={saving}
                onPress={() => picker && assign(picker, 'self')}
                style={styles.pickerRow}
                activeOpacity={0.7}
              >
                <View style={styles.pickerAvatarMaroon}>
                  <Ionicons name="person" size={18} color="#fff" />
                </View>
                <Text style={styles.pickerName}>Myself</Text>
                {picker?.assignedFleetDriverId === managerId && <Ionicons name="checkmark-circle" size={20} color={RD.money} />}
              </TouchableOpacity>

              {members.map(m => (
                <TouchableOpacity
                  key={m.id}
                  disabled={saving}
                  onPress={() => picker && assign(picker, m.id)}
                  style={styles.pickerRow}
                  activeOpacity={0.7}
                >
                  <View style={styles.avatar}>
                    <Text style={styles.avatarText}>{(m.name || '?').charAt(0).toUpperCase()}</Text>
                  </View>
                  <View style={{ flex: 1 }}>
                    <Text style={styles.pickerNameInline}>{m.name}</Text>
                    <Text style={styles.pickerSub}>{m.vehicleType || 'Driver'} · {jobsForMember(m.id)} active</Text>
                  </View>
                  {picker?.assignedFleetDriverId === m.id && <Ionicons name="checkmark-circle" size={20} color={RD.money} />}
                </TouchableOpacity>
              ))}
            </ScrollView>
            {saving && <ActivityIndicator color={RD.maroon} style={{ marginTop: 14 }} />}
          </View>
        </View>
      </Modal>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  safe: { flex: 1, backgroundColor: RD.bg },

  header: {
    paddingHorizontal: 18, paddingTop: 8, paddingBottom: 16,
    backgroundColor: RD.surface, borderBottomWidth: 1, borderColor: RD.border,
  },
  headerTitleRow: { flexDirection: 'row', alignItems: 'center', gap: 8, flexWrap: 'wrap' },
  headerTitle: { fontSize: 24, fontWeight: '700', color: RD.text, letterSpacing: -0.4 },
  managerPill: {
    flexDirection: 'row', alignItems: 'center', gap: 4,
    backgroundColor: RD.maroonSoft, paddingHorizontal: 10, paddingVertical: 4, borderRadius: 20,
  },
  managerPillText: { fontSize: 12, fontWeight: '700', color: RD.maroon },
  headerSub: { fontSize: 13, color: RD.textMuted, marginTop: 4 },

  statRow: { flexDirection: 'row', gap: 11, marginBottom: 16 },
  statCard: {
    flex: 1, padding: 14, alignItems: 'center',
    backgroundColor: RD.surface, borderRadius: 18, borderWidth: 1, borderColor: RD.border,
    shadowColor: '#000', shadowOpacity: 0.04, shadowRadius: 8, shadowOffset: { width: 0, height: 2 }, elevation: 1,
  },
  statNum: { fontSize: 24, fontWeight: '700' },
  statLabel: { fontSize: 11.5, color: RD.textMuted, marginTop: 2 },

  messagesCard: {
    flexDirection: 'row', alignItems: 'center', gap: 12, padding: 15, marginBottom: 16,
    backgroundColor: RD.surface, borderRadius: 18, borderWidth: 1, borderColor: RD.border,
    shadowColor: '#000', shadowOpacity: 0.04, shadowRadius: 8, shadowOffset: { width: 0, height: 2 }, elevation: 1,
  },
  messagesIcon: {
    width: 40, height: 40, borderRadius: 12, backgroundColor: RD.maroon,
    justifyContent: 'center', alignItems: 'center',
  },
  messagesTitle: { fontSize: 15, fontWeight: '700', color: RD.text },
  messagesSub: { fontSize: 12, color: RD.textMuted, marginTop: 1 },

  toAssignCard: {
    flexDirection: 'row', alignItems: 'center', gap: 12, padding: 15, marginBottom: 24,
    backgroundColor: RD.amberSoft, borderRadius: 18, borderWidth: 1, borderColor: RD.amber + '4D',
  },
  toAssignIcon: {
    width: 40, height: 40, borderRadius: 12, backgroundColor: RD.amber,
    justifyContent: 'center', alignItems: 'center',
  },
  toAssignTitle: { fontSize: 14, fontWeight: '700', color: RD.text },
  toAssignSub: { fontSize: 12.5, color: RD.textSec, marginTop: 1 },

  eyebrow: {
    fontSize: 12, fontWeight: '800', color: RD.textFaint, letterSpacing: 0.6, marginBottom: 10,
  },
  emptyText: { fontSize: 14, color: RD.textMuted, marginBottom: 20 },

  memberCard: {
    flexDirection: 'row', alignItems: 'center', gap: 12, padding: 15, marginBottom: 10,
    backgroundColor: RD.surface, borderRadius: 18, borderWidth: 1, borderColor: RD.border,
    shadowColor: '#000', shadowOpacity: 0.04, shadowRadius: 8, shadowOffset: { width: 0, height: 2 }, elevation: 1,
  },
  avatar: {
    width: 44, height: 44, borderRadius: 22, backgroundColor: RD.maroonSoft,
    justifyContent: 'center', alignItems: 'center',
  },
  avatarText: { fontSize: 16, fontWeight: '800', color: RD.maroon },
  memberNameRow: { flexDirection: 'row', alignItems: 'center', gap: 8, flexWrap: 'wrap' },
  memberName: { fontSize: 14.5, fontWeight: '700', color: RD.text },
  statusDotRow: { flexDirection: 'row', alignItems: 'center', gap: 5 },
  statusDot: { width: 7, height: 7, borderRadius: 4 },
  statusText: { fontSize: 11.5, fontWeight: '600' },
  memberSub: { fontSize: 12, color: RD.textMuted, marginTop: 2 },
  memberCount: { alignItems: 'flex-end' },
  memberCountNum: { fontSize: 16, fontWeight: '800', color: RD.text },
  memberCountLabel: { fontSize: 11, color: RD.textMuted },

  jobCard: {
    backgroundColor: RD.surface, borderRadius: 18, borderWidth: 1, borderColor: RD.border,
    padding: 15, marginBottom: 12,
    shadowColor: '#000', shadowOpacity: 0.04, shadowRadius: 8, shadowOffset: { width: 0, height: 2 }, elevation: 1,
  },
  jobTitle: { fontSize: 15, fontWeight: '700', color: RD.text },
  jobMeta: { fontSize: 12, color: RD.textMuted, marginTop: 3 },
  jobFooter: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginTop: 12 },
  assigneePill: {
    flexDirection: 'row', alignItems: 'center', gap: 6,
    paddingHorizontal: 10, paddingVertical: 5, borderRadius: 20,
  },
  assigneePillText: { fontSize: 12, fontWeight: '700' },
  reassignBtn: {
    flexDirection: 'row', alignItems: 'center', gap: 5,
    backgroundColor: RD.ink, paddingHorizontal: 14, paddingVertical: 8, borderRadius: 20,
  },
  reassignBtnText: { fontSize: 13, fontWeight: '700', color: '#fff' },

  modalOverlay: { flex: 1, backgroundColor: 'rgba(0,0,0,0.4)', justifyContent: 'flex-end' },
  modalSheet: {
    backgroundColor: RD.surface, borderTopLeftRadius: 20, borderTopRightRadius: 20,
    padding: 20, maxHeight: '75%',
  },
  modalHeader: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 },
  modalTitle: { fontSize: 18, fontWeight: '800', color: RD.text },
  modalSub: { fontSize: 13, color: RD.textMuted, marginBottom: 14 },
  pickerRow: {
    flexDirection: 'row', alignItems: 'center', gap: 12, paddingVertical: 14,
    borderBottomWidth: 1, borderColor: RD.border,
  },
  pickerAvatarMaroon: {
    width: 44, height: 44, borderRadius: 22, backgroundColor: RD.maroon,
    justifyContent: 'center', alignItems: 'center',
  },
  pickerName: { flex: 1, fontSize: 15, fontWeight: '700', color: RD.text },
  pickerNameInline: { fontSize: 15, fontWeight: '700', color: RD.text },
  pickerSub: { fontSize: 12, color: RD.textMuted },
});
